import React, { useMemo, useState, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { Body } from '../../Shell/Shell'
import { SET_CORRESPONDENCE2_REDUCER, initialState } from '../Correspondence2Reducer'
import FilingInfoStep, { isFilingInfoStepValid } from './FilingInfoStep'
import ContactInfoStep, { isContactInfoStepValid } from './ContactInfoStep'
import ReasonsStep, { isReasonStepValid } from './ReasonsStep'
import RefundStep, { isRefundStepValid } from './RefundStep'
import LetterTemplate from '../LetterTemplate'
import { setErrorToast } from '../../../actions'
import { pdfTypes, ADD_PDF_TO_LIST } from '../../ReportViewer/ReportViewerReducer'
import { SET_RECEIPT_WORKVIEW_ITEM } from '../../Accounting/ReceiptValidation/ReceiptValidationReducer'
import * as api from '../../../requests'
import { FilingSourceTypeIds, ProcessingStatusIds } from '../../../Utilities/Utilities'
import { SUBROUTES } from '../../../App'

const steps = [
  {
    StepId: 1,
    Name: 'Filing Information',
    component: <FilingInfoStep />,
    isValidCheck: isFilingInfoStepValid,
    Enabled: true,
    Correction: true,
    Rejection: true,
    LetterFolded: false,
  },
  {
    StepId: 2,
    Name: 'Delivery Method',
    component: <ContactInfoStep />,
    isValidCheck: isContactInfoStepValid,
    Enabled: true,
    Correction: true,
    Rejection: true,
    LetterFolded: true,
  },
  {
    StepId: 3,
    Name: 'Reasons',
    component: <ReasonsStep />,
    isValidCheck: isReasonStepValid,
    Enabled: true,
    Correction: true,
    Rejection: true,
    LetterFolded: false,
  },
  {
    StepId: 4,
    Name: 'Refund',
    component: <RefundStep />,
    isValidCheck: isRefundStepValid,
    Enabled: false,
    Correction: false,
    Rejection: true,
    LetterFolded: false,
  },
]

const CorrWizard = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { isSidebarCollapsed, activeSourceTypes } = useSelector((state) => state.app, shallowEqual)
  const corrInfo = useSelector((state) => state.correspondence2, shallowEqual)
  const { windowInstance } = useSelector((state) => state.imageViewer, shallowEqual)
  const {
    CustomCorrespondenceReasons,
    CustomCorrespondenceTypeId,
    CustomCorrespondenceTypeName,
    EntryType,
    readOnly,
    SourceTypeId,
    RecordTypeId,
    RecordSubTypeId,
    FilingTypeId,
    MiscellaneousModuleId,
    ImageNum,
    searchItem,
    sourceTypes,
    generalReasons,
    specificReasons,
    rejectionsRequireRefunds,
    workviewItem,
    ImageGroupId,
  } = corrInfo
  const [activeStep, setActiveStep] = useState(0)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [oprDescription, setOprDescription] = useState('')
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)

  useEffect(() => {
    api.GET_SOURCE_TYPE_OPTIONS(
      (data) => {
        dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { sourceTypes: data } })
      },
      (err) => console.error(err)
    )
    api.GET_APP_SETTING_BY_NAME(
      'RejectionsRequireRefunds',
      (data) => {
        dispatch({
          type: SET_CORRESPONDENCE2_REDUCER,
          payload: { rejectionsRequireRefunds: data && data.VALUE.toLowerCase() === 'true' },
        })
      },
      (err) => console.error(err)
    )

    return () => {
      dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: initialState })
    }
  }, [])

  useEffect(() => {
    if (SourceTypeId && CustomCorrespondenceTypeId && !searchItem) {
      const onReasonsSuccess = (reasonsRtn) => {
        let fullGeneralReasons = []
        let fullSpecificReasons = []
        if (reasonsRtn.GeneralReasons)
          fullGeneralReasons = reasonsRtn.GeneralReasons.map((ctg) => {
            let newReasons = ctg.Reasons.map((rsn) => {
              return { ...rsn, parent: 'GeneralReasons', checked: false }
            })
            return { ...ctg, isOpen: false, Reasons: newReasons }
          })

        if (reasonsRtn.SpecificReasons)
          fullSpecificReasons = reasonsRtn.SpecificReasons.map((ctg) => {
            let newReasons = ctg.Reasons.map((rsn) => {
              return { ...rsn, parent: 'SpecificReasons', checked: false }
            })
            return { ...ctg, isOpen: false, Reasons: newReasons }
          })

        dispatch({
          type: SET_CORRESPONDENCE2_REDUCER,
          payload: { generalReasons: fullGeneralReasons, specificReasons: fullSpecificReasons },
        })
      }
      if (+SourceTypeId === FilingSourceTypeIds.Miscellaneous) {        
        api.GET_MISCELLANEOUS_CORRESPONDING_REASONS(
          CustomCorrespondenceTypeId,
          MiscellaneousModuleId || 0,
          FilingTypeId || 0,
          onReasonsSuccess,
          (err) => console.error(err)
        )
      } else {
        api.GET_CORRESPONDING_REASONS(
          { SourceTypeId, CustomCorrespondenceTypeId, RecordTypeId, RecordSubTypeId, FilingTypeId },
          onReasonsSuccess,
          (err) => console.error(err)
        )
      }
    }
  }, [SourceTypeId, CustomCorrespondenceTypeId, searchItem, RecordTypeId, FilingTypeId, MiscellaneousModuleId])

  useEffect(() => {
    const selectedType = sourceTypes.find((type) => type.Id === +SourceTypeId)
    if (selectedType && selectedType.ModuleName !== 'All') {
      if (+SourceTypeId === FilingSourceTypeIds.Miscellaneous) {        
        const allModules = (SUBROUTES || [])
          .filter((r) => r.IsMiscRecordsType)
          .flatMap((r) => r.MiscRecordsModules || [])
          .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))        
        dispatch({
          type: SET_CORRESPONDENCE2_REDUCER,
          payload: { miscellaneousModules: allModules, recordTypes: [] },
        })             
        return
      }
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: { recordTypes: [], recordSubTypes: [], recordSubTypesXref: [], filingTypes: [] },
      })
      const promises = []
      promises.push(
        new Promise((res, rej) => {
          api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
            selectedType.ModuleName,
            'RecordType',
            (data) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { recordTypes: data },
              })
              res()
            },
            rej
          )
        })
      )
      promises.push(
        new Promise((res, rej) => {
          if (selectedType.ModuleName === 'Business')
            api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
              selectedType.ModuleName,
              'RecordSubtype',
              (data) => {
                dispatch({
                  type: SET_CORRESPONDENCE2_REDUCER,
                  payload: { recordSubTypes: data },
                })
                res()
              },
              rej
            )
          else res()
        })
      )
      promises.push(
        new Promise((res, rej) => {
          api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
            selectedType.ModuleName,
            'FilingType',
            (data) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { filingTypes: data },
              })
              res()
            },
            rej
          )
        })
      )
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: { showFilingSpinner: true },
      })
      Promise.all(promises)
        .then(() => {
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: { showFilingSpinner: false },
          })
        })
        .catch((err) => {
          console.error(err)
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: { showFilingSpinner: false },
          })
        })
    }
  }, [SourceTypeId, sourceTypes])

  const filteredSteps = useMemo(() => {
    return steps.filter(
      (step) =>
        step.Enabled && (CustomCorrespondenceTypeId === 1 ? step.Rejection : step.Correction)
    )
  }, [CustomCorrespondenceTypeId, steps, activeStep])

  useEffect(() => {
    setActiveStep(
      !EntryType || EntryType === 'Paper Filed'
        ? 0
        : readOnly
        ? 0
        : CustomCorrespondenceTypeId === 2
        ? 0
        : 1
    )
  }, [EntryType, readOnly, CustomCorrespondenceTypeId])

  useEffect(() => {
    const folded = filteredSteps.at(activeStep).LetterFolded
    dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { folded } })
  }, [filteredSteps, activeStep])

  const everyStepValid = useMemo(() => {
    return filteredSteps.every((step) => step.isValidCheck(corrInfo))
  }, [filteredSteps, corrInfo])

  const submitButtonRendering = useMemo(() => {
    return activeStep !== filteredSteps.length - 1 || readOnly
  }, [activeStep, filteredSteps])

  useEffect(() => {
    if (
      !searchItem &&
      SourceTypeId &&
      workviewItem.Filing.Id &&
      workviewItem.ProcessingStatusId !== ProcessingStatusIds.PendingSecondaryReview
    ) {
      api.GET_RECEIPT_BY_SOURCE_TYPE_ID_SOURCE_ID(
        SourceTypeId,
        workviewItem.Filing.Id,
        (receipt) => {
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: { receiptInfo: receipt },
          })
        },
        console.error
      )
    } else if (!searchItem && ImageGroupId) {
      // For Paper Filings
      api.GET_RECEIPT_NUM_BY_IMAGE_GROUP_ID(
        ImageGroupId,
        (receiptNum) => {
          api.FETCH_RECEIPT_DETAILS(
            receiptNum,
            (receipt) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { receiptInfo: receipt },
              })
            },
            console.error
          )
        },
        console.error
      )
    }
  }, [SourceTypeId, searchItem, workviewItem, ImageGroupId])

  useEffect(() => {
    api.GET_APP_SETTING_BY_NAME(
      'INTERNAL_PAYMENT_TRANSFER_DESCR',
      (data) => {
        if (!data.VALUE) {
          console.warn('App Setting INTERNAL_PAYMENT_TRANSFER_DESCR not set')
        } else {
          setOprDescription(data.VALUE.toString())
        }
      },
      (err) => {
        console.error(err)
        console.warn('App Setting INTERNAL_PAYMENT_TRANSFER_DESCR not set')
      }
    )
  }, [])

  const rejectionRedirect = () => {
    // paper filed - open existing receipt
    // web prepared (cash or check) - open existing receipt
    // web filed (OPR payment) - open existing receipt
    const { receiptInfo = {} } = corrInfo
    if (
      (EntryType === 'Paper Filed' || EntryType === 'Web Prepared') &&
      receiptInfo.Status === 'Unbalanced'
    ) {
      dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, isCorrespondence: true, workviewItem: null })
      appUserRights.includes(`ReceiptView`) &&
        history.push(`/accounting/receipt/${receiptInfo.ReceiptNum}`)
    } else if (EntryType === 'Web Filed') {
      // find a payment line with type OPR
      const found = (receiptInfo.Payments || []).find((payment) => {
        return payment.Type.Description.includes(oprDescription)
      })
      if (found) {
        dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, isCorrespondence: true, workviewItem: null })
        appUserRights.includes(`ReceiptView`) &&
          history.push(`/accounting/receipt/${receiptInfo.ReceiptNum}`)
      } else {
        history.push('/office/workview')
      }
    } else {
      history.push('/office/workview')
    }
  }

  const onSubmitError = (err) => {
    setSubmitLoading(false)
    dispatch(
      setErrorToast('Something went wrong while sending the correspondence, please try again.')
    )
  }

  const onSubmitSuccess = (response) => {
    setSubmitLoading(false)
    setSubmitSuccess(true)
    setTimeout(() => {
      if (windowInstance && windowInstance.location && windowInstance.location.reload) {
        windowInstance.location.reload() // THis may cause the image viewer to close.
      }
      if (CustomCorrespondenceTypeId === 1) {
        if (
          rejectionsRequireRefunds &&
          appUserRights.includes('ReceiptView') &&
          corrInfo.receiptInfo.ReceiptNum
        ) {
          dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, isCorrespondence: true, workviewItem: null })
          history.push(`/accounting/receipt/${corrInfo.receiptInfo.ReceiptNum}`)
        } else {
          rejectionRedirect()
        }
      } else history.push('/office/workview')
    }, 300)

    const { CustomCorrespondenceRecipient, CustomCorrespondenceNumber, CustomCorrespondenceId } =
      response
    if (CustomCorrespondenceRecipient.Address1) {
      dispatch({
        type: ADD_PDF_TO_LIST,
        pdfInfo: {
          type: pdfTypes.CORRESPONDENCE,
          filename: `${CustomCorrespondenceTypeName}-${CustomCorrespondenceNumber}.pdf`,
          correspondenceTypeId: CustomCorrespondenceTypeId,
          customCorrespondenceId: CustomCorrespondenceId,
        },
      })
    }
  }

  const handleSubmit = () => {
    if (!everyStepValid) return
    const reasonList = CustomCorrespondenceReasons.map((item) => {
      let params = null
      if (item.paramsList && item.paramsList.length > 0) {
        params = ''
        item.paramsList.forEach((param) => {
          params += `${param.key.substring(1, param.key.length - 1)}=${param.value}|`
        })
      }
      return {
        CustomCorrespondenceReasonTypeId: item.ID,
        Params: params,
        LongDescription: item.parsedReason || item.LongReason,
      }
    })
    const payload = {
      CustomCorrespondenceTypeId: corrInfo.CustomCorrespondenceTypeId,
      SourceTypeId: Number(corrInfo.SourceTypeId),
      SourceId: corrInfo.SourceId,
      RecordTypeId: Number(corrInfo.RecordTypeId),
      RecordSubTypeId:
        corrInfo.RecordSubTypeId === 'None' || corrInfo.RecordSubTypeId === null
          ? null
          : Number(corrInfo.RecordSubTypeId),
      FilingTypeId: Number(corrInfo.FilingTypeId),
      MiscellaneousModuleId: corrInfo.MiscellaneousModuleId === null ? null : Number(corrInfo.MiscellaneousModuleId),
      RecordName: corrInfo.RecordName,
      SourceNumber: corrInfo.SourceNumber,
      ProcessingStatusTypeId: corrInfo.ProcessingStatusTypeId,
      FilingDate: corrInfo.FilingDate,
      ImageNum: corrInfo.ImageNum,
      // EnclosedAmount: corrInfo.EnclosedAmount,
      // ReceiptNumber: corrInfo.ReceiptNumber,
      ImageGroupId: corrInfo.ImageGroupId,
      CustomCorrespondenceReasons: reasonList,
      CustomCorrespondenceRecipient: corrInfo.CustomCorrespondenceRecipient,
      EntryTypeId: corrInfo.EntryTypeId,
      IncludeOriginalImage: corrInfo.IncludeOriginalImage,
    }
    let moduleName = activeSourceTypes.find(
      (type) => type.SourceTypeId === Number(corrInfo.SourceTypeId)
    ).ModuleName
    // for some reason the controller name is not the module name for Facisimile.
    if (moduleName.includes('Facsimile')) moduleName = 'FacsimileSignature'
    setSubmitLoading(true)    
    api.REJECT_CORRECT_FILING(
      moduleName,
      ImageNum,
      CustomCorrespondenceTypeId === 1 ? 'rejection' : 'correction',
      () => {
        api.POST_CORRESPONDENCE(
          payload,
          (response) => {
            onSubmitSuccess(response)
          },
          onSubmitError
        )
      },
      onSubmitError
    )
  }

  useEffect(() => {
    if (generalReasons.length > 0 || specificReasons.length > 0) {
      const checkedReasons = []
      ;[...generalReasons, ...specificReasons].forEach((category) => {
        category.Reasons.forEach((r) => {
          if (r.checked)
            checkedReasons.push({
              ...r,
              CustomCorrespondenceReasonTypeId: r.ID,
              LongDescription: r.parsedReason || r.LongReason,
              ParamList: r.paramsList,
              Params: r.params,
            })
        })
      })
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          CustomCorrespondenceReasons: checkedReasons,
        },
      })
    }
  }, [generalReasons, specificReasons])

  return (
    <div className="corr-wizard">
      <div className="wizard processing">
        <div className="wrapper">
          <div className="sidebar-wrapper">
            <div className="sidebar corr-sidebar">
              {filteredSteps.map((step, index) => {
                return (
                  <StepButton
                    active={activeStep === index}
                    isValid={step.isValidCheck(corrInfo) ? true : undefined}
                    title={step.Name}
                    stepIndex={index}
                    totalSteps={filteredSteps.length}
                    setActiveStep={setActiveStep}
                    key={step.StepId}
                  />
                )
              })}
            </div>
          </div>
          <div className="corr-body">
            <Body>{filteredSteps.at(activeStep).component}</Body>
          </div>
        </div>
      </div>
      <div className="template-letter-wrapper">
        <LetterTemplate />
      </div>
      <div className={'context-bar-wrapper ' + (isSidebarCollapsed ? 'sidebar-collapsed ' : ' ')}>
        <ContextBar className="granite-context-bar">
          <div className="context-button-wrapper">
            <ContextButton variant="secondary" onClick={() => history.goBack()}>
              Cancel
            </ContextButton>
            <ContextButton
              className={`${activeStep === 0 ? 'hidden' : ''}`}
              onClick={() => {
                setActiveStep(activeStep - 1)
              }}
              variant="secondary"
            >
              Back
            </ContextButton>
            <ContextButton
              className={`${activeStep + 1 === filteredSteps.length ? 'hidden' : ''}`}
              disabled={!filteredSteps.at(activeStep).isValidCheck(corrInfo)}
              onClick={() => {
                setActiveStep(activeStep + 1)
              }}
              variant="primary"
            >
              Next
            </ContextButton>
            <button
              className={`no-padding-context-button ${
                CustomCorrespondenceTypeId === 2 ? 'correct' : 'reject'
              } ${submitButtonRendering ? ' hidden' : ''}`}
              style={{
                backgroundColor:
                  submitButtonRendering || !everyStepValid
                    ? 'grey'
                    : CustomCorrespondenceTypeId === 2
                    ? 'green'
                    : 'red',
                cursor: submitButtonRendering || !everyStepValid ? 'not-allowed' : 'default',
              }}
              onClick={handleSubmit}
              disabled={submitButtonRendering || !everyStepValid || submitLoading}
            >
              {CustomCorrespondenceTypeId === 2 ? 'Send for Correction' : 'Send Rejection'}
              {(submitLoading || submitSuccess) && (
                <div className="spinner-wrapper">
                  <input type="checkbox" id="check" disabled={true} checked={submitSuccess} />
                  <label htmlFor="check">
                    <div className="check-icon"></div>
                  </label>
                </div>
              )}
            </button>
          </div>
        </ContextBar>
      </div>
    </div>
  )
}

export default CorrWizard

const StepButton = ({ active, isValid, title, readOnly, stepIndex, totalSteps, ...props }) => {
  const setActiveStep = () => {
    props.setActiveStep(stepIndex)
  }
  return (
    <button
      className={(active ? 'active ' : ' ') + (isValid === false ? 'step-invalid' : '')}
      onClick={setActiveStep}
      aria-invalid={!isValid}
    >
      <div className={isValid || readOnly ? 'step-check step-valid' : 'step-check'}>
        {isValid === false && <div className="invalid-icon" />}
      </div>
      {title}
      <span className="visually-hidden">
        {`Step ${stepIndex + 1} of ${totalSteps}`}
        {isValid === false ? 'There are errors on this step' : ''}
      </span>
    </button>
  )
}
